// Slide 4 - AAN Publications
import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";

export default function Slide4() {
  const images = ["/aan/aan_1.png", "/aan/aan_2.png", "/aan/aan_3.png"];

  const [announcements, setAnnouncements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    const ac = new AbortController();

    fetch("/api/announcements", { signal: ac.signal })
      .then((res) => {
        if (!res.ok) throw new Error("no announcements");
        return res.json();
      })
      .then((data) => {
        if (!mounted) return;
        if (Array.isArray(data)) setAnnouncements(data);
        else if (Array.isArray(data?.announcements))
          setAnnouncements(data.announcements);
      })
      .catch(() => {
        if (!mounted) return;
        setError(true);
      })
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
      ac.abort();
    };
  }, []);

  // keywords to highlight in content
  const keywords = [
    "editorial workflows",
    "lightweight CMS",
    "responsive reading experiences",
    "content-led information architecture",
    "discoverability",
    "clarity",
  ];

  const renderHighlighted = (text) => {
    if (!text) return null;
    const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const splitRegex = new RegExp(
      `(${keywords.map((k) => esc(k)).join("|")})`,
      "gi",
    );
    const parts = text.split(splitRegex);
    return parts.map((part, i) => {
      if (!part) return null;
      const isKeyword = keywords.some(
        (k) => k.toLowerCase() === part.toLowerCase(),
      );
      return isKeyword ? (
        <span key={i} className="keyword">
          {part}
        </span>
      ) : (
        <span key={i}>{part}</span>
      );
    });
  };

  return (
    <div className="w-full max-w-[1400px] relative z-10">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
        {/* Left: Content */}
        <div className="space-y-6 bg-white/10 backdrop-blur-2xl rounded-3xl p-8 md:p-12 border border-white/20 shadow-2xl">
          <div>
            <span className="inline-block px-4 py-2 bg-white/16 backdrop-blur-md rounded-full text-white text-xs uppercase tracking-widest font-bold border border-white/20">
              Website
            </span>
            <h2
              className="text-white font-black mt-4 leading-tight drop-shadow-lg"
              style={{
                fontSize: "clamp(40px, 6vw, 68px)",
                letterSpacing: "-0.02em",
              }}
            >
              AAN Publications
            </h2>
          </div>

          {/* Announcement-driven content: fetch from backend, fallback to static copy */}
          {loading ? (
            <p className="text-white/70">Loading announcements…</p>
          ) : announcements && announcements.length > 0 ? (
            <div>
              <h3 className="text-white font-semibold">Latest announcement</h3>
              <p
                className="text-white/85 leading-relaxed mt-2"
                style={{ fontSize: "clamp(15px, 1.6vw, 20px)" }}
              >
                {renderHighlighted(
                  announcements[0].content ||
                    announcements[0].body ||
                    announcements[0].text ||
                    announcements[0].summary,
                )}
              </p>
              <ul className="mt-4 space-y-2 text-white/80 text-sm">
                {announcements.slice(0, 3).map((a, i) => (
                  <li key={i} className="flex items-center justify-between">
                    <span className="font-medium text-white">
                      {a.title || a.headline || `Announcement ${i + 1}`}
                    </span>
                    {a.date ? (
                      <span className="text-white/60 text-xs ml-3">
                        {new Date(a.date).toLocaleDateString()}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p
              className="text-white/85 leading-relaxed"
              style={{ fontSize: "clamp(15px, 1.6vw, 20px)" }}
            >
              {renderHighlighted(
                "A modern website platform with editorial workflows, a lightweight CMS, responsive reading experiences, and content-led information architecture — built for discoverability and clarity.",
              )}
            </p>
          )}

          <div className="flex items-center gap-3">
            <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
            <p className="text-white/80 text-sm font-medium">
              Delivered in 8 days
            </p>
          </div>

          <a
            href="https://aanpublishing.org"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-white text-gray-900 px-7 py-3 rounded-full font-semibold text-sm transition-all duration-300 hover:scale-105 hover:shadow-2xl active:scale-95 border border-white/30"
            style={{ boxShadow: "0 12px 40px rgba(255,255,255,0.12)" }}
          >
            Visit Website
            <ExternalLink size={16} />
          </a>
        </div>

        {/* Right: Gallery - large image with two smaller below */}
        <div className="relative h-[640px] hidden lg:flex items-start justify-center">
          <div className="w-full max-w-[880px]">
            {/* Large hero image */}
            <div className="mb-4">
              <figure className="bg-white/6 p-3 rounded-3xl border border-white/10 shadow-2xl transition-transform duration-300">
                <img
                  src={images[0]}
                  alt={`AAN Website large screenshot`}
                  className="w-full rounded-lg object-cover"
                  style={{ aspectRatio: "16/10" }}
                />
              </figure>
            </div>

            {/* Two smaller images below */}
            <div className="grid grid-cols-2 gap-4">
              <figure className="bg-white/6 p-2 rounded-2xl border border-white/10 shadow-lg transition-transform duration-300">
                <img
                  src={images[1]}
                  alt={`AAN Website screenshot 2`}
                  className="w-full rounded-lg"
                  style={{ aspectRatio: "16/9" }}
                />
              </figure>
              <figure className="bg-white/6 p-2 rounded-2xl border border-white/10 shadow-lg transition-transform duration-300">
                <img
                  src={images[2]}
                  alt={`AAN Website screenshot 3`}
                  className="w-full rounded-lg"
                  style={{ aspectRatio: "16/9" }}
                />
              </figure>
            </div>
          </div>
        </div>

        {/* Mobile: stacked large image then two smaller */}
        <div className="flex flex-col gap-4 lg:hidden">
          <div className="bg-white/6 p-3 rounded-2xl shadow-2xl">
            <img
              src={images[0]}
              alt={`AAN Website large`}
              className="w-full rounded-lg"
              style={{ aspectRatio: "16/10" }}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/6 p-2 rounded-xl shadow-2xl">
              <img
                src={images[1]}
                alt={`AAN Website screenshot 2`}
                className="w-full rounded-lg"
                style={{ aspectRatio: "16/9" }}
              />
            </div>
            <div className="bg-white/6 p-2 rounded-xl shadow-2xl">
              <img
                src={images[2]}
                alt={`AAN Website screenshot 3`}
                className="w-full rounded-lg"
                style={{ aspectRatio: "16/9" }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
